import React from 'react';
import { observer } from 'mobx-react';
import { ConnectionKnovioFormVM } from './connection-knovio-form.viewmodel';
import { FormattedMessage } from 'react-intl';
import { FieldKey, FORM_CONFIG } from './connection-knovio-form.config';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import Text from '../../../../../../global/text/text';
import { MultiSelectOption } from '../../../../../../global/multi-select-list/_multi-select-option';
import { OERadioOptions } from '../../../../../../global/oe-radio-options/oe-radio-options';
import { cancelText, submitText } from '../../../../../../../constants/common-labels.constants';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import {
    DAYS_COUNT_BEFORE_EXPIRATION_TEXT,
    EXPIRATION_DATE_IS_SET_TEXT,
    EXPIRATION_DATE_WILL_BE_SET_TEXT,
    FLYOUT_TABS_CONNECTION_LABEL_TEXT,
    NEVERLATE_ENABLED_CONNECTION_LABEL_TEXT,
    NO_EXPIRATION_DATE_IS_SET_TEXT,
    PASSPORT_REPLAY_ENABLED_CONNECTION_LABEL_TEXT,
    PUBLISHED_ENABLED_CONNECTION_LABEL_TEXT
} from '../conections-list.texts';
import { FormFieldProps } from '../../../../../../../infrastructure/form-viewmodel-controller';
import { FormFieldInput } from '../../../../../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../../../../../global/form/form-field-input/form-field-error';
import classNames from 'classnames';
import { KnovioLabel } from '../../../../../../../api/connections.dao';

export type ConnectionTemplateFormPresentationProps = {
    vm: ConnectionKnovioFormVM;
};

export const ConnectionKnovioFormPresentation = observer(({ vm }: ConnectionTemplateFormPresentationProps): JSX.Element => {
    const {
        type,
        label,
        enableNeverlate,
        standByMessage,
        layout,
        publishedEnabled,
        passportReplayEnabled,
        enableExpirationDate,
        expirationDays
    } = vm.getFormFields(FieldKey);

    const expirationDateToggleLabel = enableExpirationDate.value
        ? EXPIRATION_DATE_IS_SET_TEXT
        : NO_EXPIRATION_DATE_IS_SET_TEXT;

    const toggleField = (field: FormFieldProps) => {
        field.setValue(!field.value);
    };

    const toggleEnableExpirationDate = (): void => {
        enableExpirationDate.value ? vm.disableExpirationDate() : vm.enableExpirationDate();
    };

    const getFieldPrefix = (value: boolean): string => {
        return value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
    };

    const projectType = { ...type, value: KnovioLabel.OEStream};

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--connection-template'>
                    <FormFieldInput.Text
                        field={projectType}
                        labelDescriptor={FORM_CONFIG[FieldKey.TYPE].labelDescriptor}
                        readOnly
                        disabled
                    />
                    <FormFieldInput.Text field={label} labelDescriptor={FORM_CONFIG[FieldKey.LABEL].labelDescriptor} withError />
                    <FormFieldInput.Toggle
                        isSelected={enableNeverlate.value as boolean}
                        toggle={toggleField.bind(null, enableNeverlate)}
                        text={<Text
                            {...NEVERLATE_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                postfix: getFieldPrefix(enableNeverlate.value as boolean)
                            }}
                        />}
                        className='meeting-toggle'
                    />
                    <FormField
                        className='m-bottom-10px'
                        label={<FormattedMessage {...FLYOUT_TABS_CONNECTION_LABEL_TEXT} />}
                    >
                        <div className='oe-form__row'>
                            {
                                ConnectionKnovioFormVM.flyoutTabsOptions.map(tab => {
                                    const isSelected = vm.flyoutTabs[tab.value] as boolean;
                                    const updateFlyoutTabsOptions = vm.updateFlyoutTabsOptions.bind(vm, tab.value);

                                    return <MultiSelectOption
                                        key={tab.label}
                                        isSelected={isSelected}
                                        name={tab.label}
                                        onCheckboxClick={updateFlyoutTabsOptions}
                                        onClick={updateFlyoutTabsOptions}
                                        isCheckboxInLeft
                                    />;
                                })
                            }
                        </div>
                    </FormField>
                    <FormField
                        className='layout-options m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.LAYOUT].labelDescriptor} />}
                    >
                        <OERadioOptions
                            value={layout.value as string}
                            onOptionToggle={option => layout.setValue(option)}
                            options={ConnectionKnovioFormVM.layoutOptions}
                            isIconFirst
                        />
                    </FormField>
                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.STANDBY_MESSAGE].labelDescriptor} />}
                    >
                        <RichTextEditor
                            className='standBy-message oe-form-field__control p-0px'
                            value={standByMessage.value as string}
                            onChange={(_, editor) => standByMessage.setValue(editor.getData())}
                            onBlur={standByMessage.setTouched}
                            validationError={
                                <FormFieldError field={standByMessage} labelDescriptor={FORM_CONFIG[FieldKey.STANDBY_MESSAGE].labelDescriptor} />
                            }
                        />
                    </FormField>

                    <FormFieldInput.Toggle
                        isSelected={enableExpirationDate.value as boolean}
                        toggle={toggleEnableExpirationDate.bind(null, enableExpirationDate.value)}
                        text={<Text {...expirationDateToggleLabel} />}
                        className={classNames('meeting-toggle', {
                            'expiration-date-toggle': enableExpirationDate.value
                        })}
                    />

                    {enableExpirationDate.value &&
                        <div className='m-bottom-10px expiration-date'>
                            <FormattedMessage {...EXPIRATION_DATE_WILL_BE_SET_TEXT} />
                            <input
                                placeholder='XX'
                                type='number'
                                value={expirationDays.value as number}
                                className='oe-form-field__control m-left-4px m-right-4px'
                                onChange={e => vm.setExpirationDays(e.target.value)}
                                onBlur={expirationDays.setTouched}
                                step='1'
                            />
                            <FormattedMessage
                                {...DAYS_COUNT_BEFORE_EXPIRATION_TEXT}
                                values={{
                                    prefix: ''
                                }}
                            />
                            <div>
                                <FormFieldError field={expirationDays} labelDescriptor={FORM_CONFIG[FieldKey.EXPIRATION_DAYS].labelDescriptor} />
                            </div>
                        </div>
                    }

                    <FormFieldInput.Toggle
                        isSelected={publishedEnabled.value as boolean}
                        toggle={toggleField.bind(null, publishedEnabled)}
                        text={<Text
                            {...PUBLISHED_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                postfix: publishedEnabled.value ? 'Published' : 'Unpublished'
                            }}
                        />}
                        className='meeting-toggle'
                    />

                    <FormFieldInput.Toggle
                        isSelected={passportReplayEnabled.value as boolean}
                        toggle={toggleField.bind(null, passportReplayEnabled)}
                        text={<Text
                            {...PASSPORT_REPLAY_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                postfix: getFieldPrefix(passportReplayEnabled.value as boolean)
                            }}
                        />}
                        className='meeting-toggle'
                    />
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={() => vm.submitForm()}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                </div>
            }
        />
    );
});
